// MultiSelectDropdown — compact filter control for the Dev Tracker bar.
// Label + count badge, "All" default, checkbox list, clear (×). Closes on
// outside-click. Values are opaque strings; options can carry a colour dot.

import { useEffect, useRef, useState, useMemo } from "react";
import { ChevronDown, X, Check } from "lucide-react";

export default function MultiSelectDropdown({
  label,
  options,          // [{ value, label, colour? }]
  selected = [],    // array of selected values
  onChange,         // (nextArray) => void
  testid = "ms",
  width = "min-w-[150px]",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const selSet = useMemo(() => new Set(selected), [selected]);
  const toggle = (v) => onChange(selSet.has(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const clear = (e) => { e.stopPropagation(); onChange([]); };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? options.filter((o) => (o.label || "").toLowerCase().includes(s)) : options;
  }, [options, q]);

  const count = selected.length;

  return (
    <div className="relative" ref={ref} data-testid={`filter-${testid}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid={`filter-${testid}-trigger`}
        className={`inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg border text-xs ${width} justify-between transition-colors ${
          count
            ? "border-orange-400/40 bg-orange-500/10 text-orange-100"
            : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20"
        }`}
      >
        <span className="inline-flex items-center gap-1.5 truncate">
          <span className="font-mono uppercase tracking-widest text-[10px] text-slate-400">{label}</span>
          {count > 0 && (
            <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-orange-500/30 text-orange-100 text-[10px] font-semibold">
              {count}
            </span>
          )}
        </span>
        {count > 0 ? (
          <span onClick={clear} className="opacity-70 hover:opacity-100" data-testid={`filter-${testid}-clear`}>
            <X className="w-3.5 h-3.5" />
          </span>
        ) : (
          <ChevronDown className="w-3.5 h-3.5 opacity-60" />
        )}
      </button>

      {open && (
        <div
          className="absolute z-50 mt-1.5 left-0 min-w-[220px] max-w-[300px] rounded-lg border border-white/10 bg-slate-900/95 backdrop-blur-sm shadow-2xl p-1.5"
          data-testid={`filter-${testid}-popover`}
        >
          {options.length > 8 && (
            <input
              autoFocus type="text" placeholder="Filter…" value={q}
              onChange={(e) => setQ(e.target.value)}
              className="input-dark w-full text-sm mb-1.5"
            />
          )}
          <div className="max-h-64 overflow-y-auto space-y-0.5">
            {filtered.map((o) => {
              const active = selSet.has(o.value);
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => toggle(o.value)}
                  data-testid={`filter-${testid}-opt-${String(o.value).replace(/\s+/g, "-")}`}
                  className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded text-sm ${
                    active ? "bg-white/[0.06] text-white" : "text-slate-300 hover:bg-white/5"
                  }`}
                >
                  <span className={`w-4 h-4 rounded border grid place-items-center shrink-0 ${
                    active ? "bg-orange-500/80 border-orange-400" : "border-white/20"
                  }`}>
                    {active && <Check className="w-3 h-3 text-white" />}
                  </span>
                  {o.colour && <span className="w-2 h-2 rounded-full shrink-0" style={{ background: o.colour }} />}
                  <span className="flex-1 truncate">{o.label}</span>
                </button>
              );
            })}
            {filtered.length === 0 && <div className="text-[11px] text-slate-500 px-2 py-2">No options</div>}
          </div>
        </div>
      )}
    </div>
  );
}
